import { Controller } from '@hotwired/stimulus'

const STORAGE_KEY = 'deleted_notification_ids'
const NOTIFICATION_ID_PREFIX = 'notification_'
const TURBO_STREAM_TAG = 'TURBO-STREAM'

function readDeletedIds() {
  try {
    const storedIds = globalThis.localStorage.getItem(STORAGE_KEY)
    const parsedIds = JSON.parse(storedIds ?? '[]')

    return Array.isArray(parsedIds)
      ? parsedIds.filter(
          id =>
            typeof id === 'string' &&
            id.startsWith(NOTIFICATION_ID_PREFIX)
        )
      : []
  } catch (error) {
    console.error('[Notifications] Failed to read deleted IDs', error)
    return []
  }
}

function saveDeletedIds(ids) {
  try {
    globalThis.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...ids])
    )
  } catch (error) {
    console.error('[Notifications] Failed to save deleted IDs', error)
  }
}

export default class extends Controller {
  connect() {
    this.deletedIds = new Set(readDeletedIds())

    this.handleStream = this.handleStream.bind(this)
    this.handleRestore = this.handleRestore.bind(this)

    this.registerEventListeners()
    this.removeDeletedNotifications()
  }

  disconnect() {
    this.removeEventListeners()
    this.observer?.disconnect()
    this.observer = null
  }

  registerEventListeners() {
    document.addEventListener('turbo:before-stream-render', this.handleStream, true)
    document.addEventListener('turbo:load', this.handleRestore)
    globalThis.addEventListener('pageshow', this.handleRestore)
  }

  removeEventListeners() {
    document.removeEventListener('turbo:before-stream-render', this.handleStream, true)
    document.removeEventListener('turbo:load', this.handleRestore)
    globalThis.removeEventListener('pageshow', this.handleRestore)
  }

  handleStream(event) {
    const stream = event.target

    if (stream?.tagName !== TURBO_STREAM_TAG) return
    if (stream.action !== 'remove') return

    const notificationId = stream.target
    if (!notificationId?.startsWith(NOTIFICATION_ID_PREFIX)) return

    this.rememberDeletedNotification(notificationId)
  }

  rememberDeletedNotification(notificationId) {
    if (this.deletedIds.has(notificationId)) return

    this.deletedIds.add(notificationId)
    saveDeletedIds(this.deletedIds)
  }

  handleRestore() {
    this.syncDeletedIds()
    this.removeDeletedNotifications()
  }

  syncDeletedIds() {
    for (const notificationId of readDeletedIds()) {
      this.deletedIds.add(notificationId)
    }
  }

  removeDeletedNotifications() {
    this.syncDeletedIds()
    for (const notificationId of this.deletedIds) {
      const notification = document.getElementById(notificationId)
      if (!notification) continue

      notification.remove()
    }
  }
}
